/**
 * The single styling-root every web app mounts at its tree root.
 *
 * Owns the Emotion cache, MUI `ThemeProvider`, and `CssBaseline` so that apps
 * never import `@emotion/*` or MUI's `ThemeProvider`/`CssBaseline` directly
 * (enforced by ESLint `no-restricted-imports`). Each app passes its finished
 * theme (from this same library) and a unique `cacheKey`.
 */
import { type ReactNode } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { CssBaseline, ThemeProvider, type Theme } from '@mui/material';

/**
 * One cache per key, for the lifetime of the module.
 *
 * A cache owns the `<style>` tags it injects into `document.head` and the
 * registry of what it has already inserted, and it never removes them — so a
 * cache built per mount means a fresh copy of the whole MUI stylesheet in
 * `head` every time this provider mounts. The app mounts it once and never
 * noticed. Tests mount it once per `render()`, and the tags piled up: jsdom
 * resolves styles proportional to the number of sheets, so every DOM query and
 * style insertion got slower as the file went on — quadratic in tests per file.
 * One spec drove the same assertion from 1s in its first test to 28s in its
 * fiftieth, and the file from seconds to ~24 minutes of CI.
 *
 * Keying the cache here rather than per instance matches what `cacheKey`
 * already promises — "unique per app" — so two providers sharing a key were
 * always meant to share a cache. Nothing changes for the apps, which mount one
 * provider; repeat mounts now reuse the tags instead of duplicating them.
 */
const caches = new Map<string, ReturnType<typeof createCache>>();

function cacheFor(key: string) {
  let cache = caches.get(key);
  if (!cache) {
    cache = createCache({ key, prepend: true });
    caches.set(key, cache);
  }
  return cache;
}

export interface AppThemeProviderProps {
  /** Finished MUI theme for the app (e.g. `webTheme`, `adminTheme`, `govTheme`). */
  theme: Theme;
  /** Emotion cache key — must be unique per app to avoid class-name collisions. */
  cacheKey: string;
  children: ReactNode;
}

export function AppThemeProvider({ theme, cacheKey, children }: AppThemeProviderProps) {
  return (
    <CacheProvider value={cacheFor(cacheKey)}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
